/**
 * Timezone management utilities for Wordle 6 7
 * Implements timezone locking: timezone only updates at midnight of the stored timezone
 */

const TIMEZONE_KEY = 'wordle6-timezone';
const TIMEZONE_LAST_CHECK_KEY = 'wordle6-timezone-last-check';

/**
 * Get the user's current device timezone
 */
export function getCurrentTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Get the stored timezone, or detect and store current timezone if not set
 */
export function getStoredTimezone() {
  let stored = localStorage.getItem(TIMEZONE_KEY);

  if (!stored) {
    stored = getCurrentTimezone();
    localStorage.setItem(TIMEZONE_KEY, stored);
    localStorage.setItem(TIMEZONE_LAST_CHECK_KEY, new Date().toISOString());
  }

  return stored;
}

/**
 * Check if it's past midnight in the stored timezone
 * If yes, update stored timezone to current device timezone
 * @returns {string} The timezone to use for today's game
 */
export function getActiveTimezone() {
  const storedTimezone = getStoredTimezone();
  const lastCheck = localStorage.getItem(TIMEZONE_LAST_CHECK_KEY);

  if (!lastCheck) {
    // First time, just use stored timezone
    localStorage.setItem(TIMEZONE_LAST_CHECK_KEY, new Date().toISOString());
    return storedTimezone;
  }

  // Check if it's past midnight in the stored timezone since last check
  const now = new Date();
  const lastCheckDate = new Date(lastCheck);

  // Get the date in the stored timezone for both times
  const lastCheckLocalDate = getDateInTimezone(lastCheckDate, storedTimezone);
  const nowLocalDate = getDateInTimezone(now, storedTimezone);

  // If the date has changed in the stored timezone, update to current timezone
  if (nowLocalDate > lastCheckLocalDate) {
    const currentTimezone = getCurrentTimezone();
    localStorage.setItem(TIMEZONE_KEY, currentTimezone);
    localStorage.setItem(TIMEZONE_LAST_CHECK_KEY, now.toISOString());
    return currentTimezone;
  }

  return storedTimezone;
}

/**
 * Get the date string (YYYY-MM-DD) in a specific timezone
 * @param {Date} date - The date to convert
 * @param {string} timezone - IANA timezone identifier
 * @returns {string} Date string in YYYY-MM-DD format
 */
export function getDateInTimezone(date, timezone) {
  const options = {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  };

  const formatter = new Intl.DateTimeFormat('en-CA', options); // en-CA gives YYYY-MM-DD format
  return formatter.format(date);
}

/**
 * Get the current date in the active timezone
 * @returns {string} Date string in YYYY-MM-DD format
 */
export function getTodayInActiveTimezone() {
  const timezone = getActiveTimezone();
  return getDateInTimezone(new Date(), timezone);
}

/**
 * Reset timezone (for testing/debugging)
 */
export function resetTimezone() {
  localStorage.removeItem(TIMEZONE_KEY);
  localStorage.removeItem(TIMEZONE_LAST_CHECK_KEY);
}
